import { useCallback, useEffect, useRef, useState } from 'react';
import { cloneDeep, isNil } from 'lodash-es';
import { devLog, isProd } from '@strava/utils/src/dev.utils';
import { getMobileOS, isMobile } from '@strava/utils/src/utils';
import { MOBILE_OS } from '@strava/constants/src/appConstants';
import { useSafeState } from '../use-safe-state';

import { isFalsePositive } from './useBranchIO.hook.helpers';

/**
 * This hook initializes branch web SDK and exposes different API endpoints
 * for transacting with the SDK.
 * */
export const useBranchIO = ({
  cookiesAccepted, // note: branch is not initialized when this value is false or undefined
  logError: _logError,
  shouldUseLegacyBranch = false, // if we're relying on branch being initialized in active
  dataSharingOptedOut,
  defaultAppLink = '/mobile'
} = {}) => {
  const [hasApp, setHasApp] = useSafeState(false);

  const [appLink, setAppLink] = useState(defaultAppLink);

  const [error, setError] = useSafeState(null);
  const [isProcessing, setIsProcessing] = useSafeState(true);

  const appLinkGenerated = useRef(false);

  // override logError to include hook specific error logging rules
  const logError = useCallback(
    (e, context = {}) => {
      const captureContext = {
        contexts: {
          'branch-sdk': {
            ...context,
            hasApp,
            isProcessing,
            shouldUseLegacyBranch,
            dataSharingOptedOut,
            cookiesAccepted,
            'navigator.cookiesAccepted': navigator?.cookieEnabled
          }
        },
        tags: {
          'hook.name': 'use-branch-io'
        }
      };

      if (
        !isFalsePositive({ shouldUseLegacyBranch, errorMessage: e.message })
      ) {
        _logError(e, captureContext);
      }
    },
    // deps are ignored on purpose to avoid unnecessary re-renders since this callback
    // is functioning as a util function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const createLink = useCallback(
    ({ feature = 'unknown', data } = {}) => {
      // only generate a appLink once
      if (appLinkGenerated.current === true) {
        // an appLink is meant to be generated once per page load and
        // shared across components through `BranchContext` in @strava/container-context
        logError(new Error('[useBranchIO] appLink was already created'));
        return;
      }

      const linkData = {
        channel: isMobile() ? 'mobile_web' : 'web',
        feature,
        data
      };

      if (!error && window.branch) {
        // cloning the props avoids unnecessary re-renders since branch updates this hash
        const clonedLinkData = cloneDeep(linkData);

        /**
         * linkData.data
         *  - add default strava_deeplink_url if not present
         *  - add fallback desktop url
         */
        if (isNil(clonedLinkData.data)) {
          clonedLinkData.data = {};
        }
        if (isNil(clonedLinkData.data.strava_deeplink_url)) {
          clonedLinkData.data.strava_deeplink_url = 'strava://';
        }
        clonedLinkData.data.$desktop_url = `${window.location.origin}/register`;

        window.branch.link(clonedLinkData, (err, link) => {
          if (err) {
            logError(err, {
              method: 'createLink',
              linkData: JSON.stringify(clonedLinkData),
              'linkData.tags': JSON.stringify(clonedLinkData?.tags)
            });
            setError(err);
          }
          devLog(
            `[useBranchIO] successfully created appLink - ${link} ::: { linkData: ${JSON.stringify(
              clonedLinkData
            )}, dataSharingOptedOut: ${dataSharingOptedOut} }`
          );
          appLinkGenerated.current = true;
          setAppLink(link);
        });
      } else {
        devLog(
          `[useBranchIO] appLink not created ::: { cookiesAccepted: ${cookiesAccepted}, branchInitialized? : ${!!window.branch}, dataSharingOptedOut: ${dataSharingOptedOut}}`
        );
      }
    },

    // appLink is not added to the dep list since this function updates it which
    // would consequently cause this function to be triggered again.

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [error, logError, setError, cookiesAccepted]
  );

  const processBranchData = useCallback(
    (err, data, method) => {
      if (err) {
        logError(err, { method });
        setError(err);
      } else if (data?.['has_app']) {
        setHasApp(true);
      }

      setIsProcessing(false);
    },
    [logError, setError, setHasApp, setIsProcessing]
  );

  useEffect(() => {
    if (!_logError) {
      const e = new Error('hook requires `logError` callback');
      devLog(e.message);
      setError(e);
      return;
    }

    if (!cookiesAccepted) {
      // use direct link to app store if user has not accepted cookies (branch is not initialized)
      const os = getMobileOS();
      if (os === MOBILE_OS.android) {
        setAppLink('https://play.google.com/store/apps/details?id=com.strava');
      } else if (os === MOBILE_OS.iPhone) {
        setAppLink(
          'https://itunes.apple.com/app/strava-cycling/id426826309?mt=8'
        );
      }

      setIsProcessing(false);
      return;
    }

    if (window.branch) {
      devLog('[useBranchIO] reusing an already installed instance of branch');
      window.branch.data((err, data) => {
        setIsProcessing(true);
        processBranchData(err, data, 'data');
      });
    } else if (!shouldUseLegacyBranch) {
      devLog(
        '[useBranchIO] branch was not found on page. Initializing a new shared instance...'
      );

      setIsProcessing(true);

      const initBranchSDK = async () => {
        // dynamically import branch-sdk
        // this is necessary for nextJS SSR since useEffect hook are always executed on the client-side
        // therefore the window object will be available for the code inside those methods.
        window.branch = (await import('branch-sdk')).default;

        const key = isProd()
          ? 'key_live_lmpPsfj2DP8CflI4rmzfiemerte7sgwm'
          : 'key_test_dfiHrehXENYvpoG6DjClFljaBvf0DjrE';

        const options = {
          retries: 3
        };

        if (dataSharingOptedOut) {
          options.tracking_disabled = true;
        }

        window.branch.init(key, options, (err, data) => {
          processBranchData(err, data, 'init');
        });
      };

      initBranchSDK();
    } else {
      logError(
        '[useBranchIO] need event listener for BranchInitialized to call handleLegacyBranchInit'
      );
      setIsProcessing(false);
    }
  }, [
    _logError,
    setIsProcessing,
    setError,
    logError,
    cookiesAccepted,
    processBranchData,
    dataSharingOptedOut,
    shouldUseLegacyBranch
  ]);

  // if we have to wait for active to initialize branch
  const handleLegacyBranchInit = useCallback(
    (event) => {
      const { err, data } = event.detail;
      processBranchData(err, data, 'legacy_init');
    },
    [processBranchData]
  );

  return {
    isProcessing,
    hasApp,
    handleLegacyBranchInit,
    createLink,
    error,
    appLink,
    cookiesAccepted
  };
};

export default useBranchIO;
